import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useState } from 'react';
import Modal from 'react-modal';

import CertificationList from '../../components/certificationList/CertificationList';
import {
  Info,
  SectionsTitle,
  SectionsTitleWrapper,
  Text,
  TitleLine,
} from '../../components/errorBoundary/ErrorBoundary.styles';
import Hero from '../../components/hero/Hero';
import HeroSupplier from '../../components/hero/HeroSupplier';
import LandingFormWrapper from '../../components/landingFormWrapper/LandingFormWrapper';
import Layout from '../../components/layout/Layout';
import PageWrapper from '../../components/pageWrapper/PageWrapper';
import {
  convertEquipmentsToSupplierEditTree,
  convertSupplierTreeEditToTreeView,
} from '../../components/tree/converters';
import TreeSupplierView from '../../components/tree/TreeSupplierView';
import {
  Image,
  Thumb,
  ThumbInner,
} from '../../components/upload/Upload.styles';
import { decodeText } from '../../modules/common';
import { fromEnum } from '../../modules/enums';
import { userBasicInfoPublicPages } from '../../modules/graphqlQueries';
import { spacing } from '../../modules/metrics';
import { fetchPublicGraphql } from '../../modules/publicSuppliers';
import { SITE_ORIGIN } from '../../modules/siteUrl';
import { useLoadData } from '../../modules/useLoadData';
import {
  DescriptionCard,
  Heading,
  IconModal,
  ImageDescription,
  LeftColumn,
  Link,
  ModalContent,
  ModalImage,
  Photos,
  PhotosContent,
  RightColumn,
} from '../../styles/supplierProfile.styles';
import { UserType } from '../../types/UserTypes';

const customStyles = {
  content: {
    padding: spacing.xl,
  },
};

interface PublicSupplierProfileProps {
  initialSupplier: UserType | null;
  initialError: string | null;
  alias: string;
}

const getBaseUrl = () => SITE_ORIGIN;

const supplierQuery = (aliasVariable: string) => `{
  supplier(alias: "${aliasVariable
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')}") {
    status
    subscriptionStatus
    companyName
    alias
    nationality
    headquarters
    staff
    phone
    logo
    description
    website
    photos{
      label
      url
    }
    ports {
      name
      country
    }
    certifications {
      _id
      title
      type
      expiration
      file
      status
    }
    equipments {
      title
      category
      subcategory
      standardServices
      customServices
      makers
    }
    travelling
    travellingCountries
    rating
    ratingCount
  }
}`;

const getViewerQuery = (userId: string) => `{
  user(_id: "${userId}") {
    ${userBasicInfoPublicPages}
  }
}`;

const fetchSupplier = async (alias: string): Promise<UserType | null> => {
  const result = await fetchPublicGraphql<{ supplier: UserType | null }>(
    supplierQuery(alias)
  );

  if (result.errors?.length) {
    // The API reports an unknown alias as an error with a null supplier.
    if (result.errors[0].message === 'Supplier not found') return null;
    throw new Error(result.errors[0].message);
  }

  return result.data?.supplier || null;
};

const PublicSupplierProfile = ({
  initialSupplier,
  initialError,
  alias,
}: PublicSupplierProfileProps): JSX.Element => {
  // getServerSideProps 404s missing/unapproved/private profiles, so the
  // page only ever renders a public supplier or an API error.
  const supplier = initialSupplier;
  const error = initialError;

  // The signed-in viewer (if any), so the header shows their menu not Login.
  const {
    isLoading: isViewerLoading,
    response: {
      data: { user: viewer },
    },
  } = useLoadData(getViewerQuery);

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [openedFile, setOpenedFile] = useState<string>('');
  const [openedFileDescripion, setOpenedFileDescription] = useState<string>('');

  const openModal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);

  const findFileType = (file: {
    url: string;
  }): { fileIcon?: string; fileType: 'img' | 'doc' } => {
    let fileType: 'img' | 'doc';
    let fileIcon: string;

    if (file.url.toLowerCase().endsWith('.pdf')) {
      fileIcon = '/pdfLogo.png';
      fileType = 'doc';
    } else if (
      file.url.toLowerCase().endsWith('.docx') ||
      file.url.toLowerCase().endsWith('.doc')
    ) {
      fileIcon = '/wordLogo.png';
      fileType = 'doc';
    } else if (
      file.url.toLowerCase().endsWith('.xls') ||
      file.url.toLowerCase().endsWith('.xlsx')
    ) {
      fileIcon = '/excel.png';
      fileType = 'doc';
    } else {
      fileType = 'img';
    }

    return { fileIcon, fileType };
  };

  const openFile = (
    file: { url: string; label: string },
    fileType: 'img' | 'doc'
  ) => {
    if (fileType === 'img') {
      openModal();
      setOpenedFile(file.url);
    } else {
      window.open(file.url);
    }
    setOpenedFileDescription(file.label);
  };

  // Show errors
  if (error || !supplier) {
    return (
      <Layout
        user={viewer as UserType}
        isLoading={false}
        isUserLoading={isViewerLoading}
      >
        <Head>
          <title>Error Loading Supplier</title>
          <meta name="robots" content="noindex, nofollow" />
        </Head>
        <LandingFormWrapper size={500}>
          <SectionsTitleWrapper>
            <SectionsTitle>Error Loading Supplier</SectionsTitle>
          </SectionsTitleWrapper>
          <TitleLine />
          <Info>
            <Text>{error || 'Failed to load supplier'}</Text>
            <Text>
              <Link href="/">← Back to Home</Link>
            </Text>
          </Info>
        </LandingFormWrapper>
      </Layout>
    );
  }

  const pageTitle = `${supplier.companyName} - Marine Services Supplier | Bid2Board`;

  // Enhanced SEO description with keywords and location
  const services =
    supplier.equipments
      ?.map((eq) => eq.category)
      .filter(Boolean)
      .map(fromEnum) || [];
  const uniqueServices = Array.from(new Set(services)).slice(0, 3);
  const servicesText =
    uniqueServices.length > 0 ? uniqueServices.join(', ') : 'marine services';
  const locationText =
    supplier.ports && supplier.ports.length > 0
      ? `in ${supplier.ports
          .map((p) => p.name)
          .slice(0, 2)
          .join(', ')}`
      : 'worldwide';

  const description = supplier.description
    ? `${decodeText(supplier.description).substring(
        0,
        120
      )}... Professional marine services including ${servicesText} ${locationText}.`
    : `Professional marine services supplier ${
        supplier.companyName
      } specializing in ${servicesText} ${locationText}.${
        supplier.ratingCount
          ? ` Rated by ${supplier.ratingCount} ship managers on Bid2Board.`
          : ''
      }`;

  // Keywords for SEO
  const keywords = [
    'marine services',
    'ship repair',
    'maritime supplier',
    'vessel maintenance',
    'port services',
    'marine contractor',
    supplier.companyName,
    supplier.nationality,
    ...uniqueServices,
    ...(supplier.ports?.map((p) => `${p.name} marine services`) || []),
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Layout
      user={viewer as UserType}
      isLoading={false}
      isUserLoading={isViewerLoading}
    >
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="author" content={supplier.companyName} />

        {/* Geographic targeting */}
        {supplier.nationality && (
          <meta name="geo.region" content={supplier.nationality} />
        )}
        {supplier.ports && supplier.ports.length > 0 && (
          <meta
            name="geo.placename"
            content={supplier.ports
              .map((p) => `${p.name}, ${p.country}`)
              .join('; ')}
          />
        )}

        {/* Business/Local SEO */}
        <meta name="business.category" content="Marine Services" />
        <meta name="business.type" content="Marine Service Supplier" />
        {supplier.rating && (
          <meta name="rating" content={supplier.rating.toString()} />
        )}
        {supplier.ratingCount && (
          <meta name="review.count" content={supplier.ratingCount.toString()} />
        )}

        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} key="og:title" />
        <meta
          property="og:description"
          content={description}
          key="og:description"
        />
        <meta property="og:type" content="business.business" key="og:type" />
        <meta property="og:site_name" content="Bid2Board" />
        {supplier.logo && (
          <meta property="og:image" content={supplier.logo} key="og:image" />
        )}
        {supplier.logo && (
          <meta
            property="og:image:alt"
            content={`${supplier.companyName} logo`}
          />
        )}

        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@bid2board" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
        {supplier.logo && <meta name="twitter:image" content={supplier.logo} />}

        {/* Canonical URL */}
        <link
          rel="canonical"
          href={`${getBaseUrl()}/s/${supplier.alias || alias}`}
        />

        {/* JSON-LD Structured Data */}
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: supplier.companyName,
              description,
              url: `${getBaseUrl()}/s/${supplier.alias || alias}`,
              logo: supplier.logo || undefined,
              address:
                supplier.ports && supplier.ports.length > 0
                  ? {
                      '@type': 'PostalAddress',
                      addressLocality: supplier.ports[0].name,
                      addressCountry: supplier.ports[0].country,
                    }
                  : undefined,
              aggregateRating:
                supplier.rating && supplier.ratingCount
                  ? {
                      '@type': 'AggregateRating',
                      ratingValue: supplier.rating,
                      reviewCount: supplier.ratingCount,
                    }
                  : undefined,
              hasOfferCatalog: {
                '@type': 'OfferCatalog',
                name: 'Marine Services',
                itemListElement: uniqueServices.map((service) => ({
                  '@type': 'Offer',
                  itemOffered: {
                    '@type': 'Service',
                    name: service,
                  },
                })),
              },
            }),
          }}
        />
      </Head>

      <Hero title="Overview">
        <HeroSupplier user={supplier} isShippingCompany={false} isPublic />
      </Hero>
      <PageWrapper>
        <LeftColumn>
          {Boolean(supplier?.photos.length) && (
            <Photos>
              <Heading extraSpacing>Photos &amp; brochure</Heading>
              <PhotosContent>
                {supplier.photos?.map((file) => {
                  const fileInfo = findFileType(file);
                  return (
                    <Thumb key={file.url} data-tip={file.label}>
                      <ThumbInner>
                        <Image
                          src={
                            fileInfo.fileType === 'img'
                              ? file.url
                              : fileInfo.fileIcon
                          }
                          onClick={() => {
                            openFile(file, fileInfo.fileType);
                          }}
                          fileType={fileInfo.fileType}
                        />
                      </ThumbInner>
                    </Thumb>
                  );
                })}
              </PhotosContent>

              <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                style={customStyles}
              >
                <IconModal
                  type="close"
                  size="lg"
                  color="black"
                  onClick={() => closeModal()}
                />
                <ModalContent>
                  <div>
                    {openedFileDescripion && (
                      <ImageDescription>
                        {openedFileDescripion}
                      </ImageDescription>
                    )}
                    <ModalImage src={openedFile} />
                  </div>
                </ModalContent>
              </Modal>
            </Photos>
          )}

          {supplier?.description && (
            <div>
              <Heading extraSpacing>Company description</Heading>
              <DescriptionCard>
                {decodeText(supplier.description)}
              </DescriptionCard>
            </div>
          )}

          {(supplier.website || supplier.phone) && (
            <div>
              <Heading extraSpacing>Contact Info</Heading>
              <DescriptionCard>
                {Boolean(supplier.phone) && (
                  <div style={{ marginBottom: supplier.phone ? '8px' : '0' }}>
                    Phone:{' '}
                    <Link href={`tel:${supplier.phone}`}>{supplier.phone}</Link>
                  </div>
                )}
                {Boolean(supplier.website) && (
                  <div>
                    Website:{' '}
                    <Link
                      href={
                        supplier.website.startsWith('http')
                          ? supplier.website
                          : `https://${supplier.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {supplier.website}
                    </Link>
                  </div>
                )}
              </DescriptionCard>
            </div>
          )}
        </LeftColumn>

        <RightColumn>
          <Heading extraSpacing>Certifications</Heading>
          <CertificationList
            certifications={supplier.certifications}
            activeOnly
            clickable={false}
            useColors={false}
          />

          <Heading extraSpacing>Services</Heading>
          <TreeSupplierView
            structure={convertSupplierTreeEditToTreeView(
              convertEquipmentsToSupplierEditTree(supplier.equipments)
            )}
          />
          <Heading extraSpacing>More suppliers</Heading>
          <DescriptionCard>
            <Link href="/suppliers">
              Browse all marine service suppliers on Bid2Board →
            </Link>
          </DescriptionCard>
        </RightColumn>
      </PageWrapper>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps<
  PublicSupplierProfileProps
> = async ({ params, res }) => {
  const alias = typeof params?.alias === 'string' ? params.alias : '';

  if (!alias) {
    return {
      notFound: true,
    };
  }

  try {
    const supplier = await fetchSupplier(alias);

    // Missing, unapproved and private profiles must be real 404s, not
    // 200 + noindex soft-404s, so crawlers drop them cleanly.
    if (
      !supplier ||
      supplier.status !== 'APPROVED' ||
      supplier.subscriptionStatus !== 'ACTIVE'
    ) {
      return {
        notFound: true,
      };
    }

    res.setHeader(
      'Cache-Control',
      'public, s-maxage=3600, stale-while-revalidate=86400'
    );

    return {
      props: {
        initialSupplier: supplier,
        initialError: null,
        alias,
      },
    };
  } catch (err) {
    // Transient API failure: 503 tells crawlers to retry later instead of
    // indexing the error page.
    res.statusCode = 503;
    return {
      props: {
        initialSupplier: null,
        initialError:
          err instanceof Error ? err.message : 'Failed to load supplier',
        alias,
      },
    };
  }
};

export default PublicSupplierProfile;
